import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, RefreshCw, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtMT } from "@/lib/mountainTime";
import { EventTest, GoLiveChecklist } from "@/components/bids/SmsTestTools";
import { SmsTestSession } from "@/components/bids/SmsTestSession";
import { smsStatus, smsVerify, smsControl, smsCredentials, smsTest, smsTests, smsRefresh, smsPreviewAuthorize, smsPreviewSend, errText } from "@/lib/bidCenterApi";

const TSTATUS = { blocked_preview: "Blocked — Preview never sends SMS", not_configured: "Twilio not configured", paused: "SMS switched off", sending: "Sending…",
  sent: "Accepted by Twilio", failed: "Failed", unknown: "Unknown — check status" };
const DELIVERY = { queued: "Queued at Twilio", accepted: "Accepted", sending: "Sending to carrier", sent: "Sent to carrier", delivered: "Delivered to phone",
  undelivered: "Not delivered", failed: "Failed" };

function PreviewTest({ st, rc, onSent }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const req = useRef(null);
  const a = st.preview_test_auth;
  const authorize = async () => {
    setBusy(true);
    try { await smsPreviewAuthorize(code); setCode(""); toast.success("Authorized: one test text, valid 15 minutes."); onSent(); } catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };
  const send = async () => {
    if (!window.confirm(`Send ONE real test text to ${rc.name} (…${rc.phone.slice(-4)}) now?`)) return;
    req.current = req.current || crypto.randomUUID();
    setBusy(true);
    try {
      const r = await smsPreviewSend({ authorization_id: a.id, request_id: req.current });
      toast.message(TSTATUS[r.status] || r.status); onSent();
      [5000, 15000, 40000].forEach((ms) => setTimeout(() => smsRefresh(r.id).then(onSent).catch(() => {}), ms));
    } catch (e) { toast.error(errText(e)); onSent(); } finally { req.current = null; setBusy(false); }
  };
  return (
    <div data-testid="sms-preview-test" className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] p-2 space-y-2">
      <p className="text-xs text-[#92400E]"><b>One-time Preview test.</b> Sends exactly one plain test text to the designated test phone. Automatic auction SMS stay blocked.</p>
      {!a ? <div className="flex gap-2">
        <Input data-testid="sms-preview-confirm" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Type SEND ONE" className="h-10 sm:h-9" />
        <Button data-testid="sms-preview-authorize" variant="outline" disabled={busy || !rc || code.trim().toUpperCase() !== "SEND ONE"} onClick={authorize} className="h-10 sm:h-9 shrink-0">Authorize one text</Button>
      </div> : <p data-testid="sms-preview-authorized" className="text-xs text-emerald-800">Authorized by {a.by} · expires {fmtMT(a.expires_at)}</p>}
      <Button data-testid="sms-test-send" disabled={busy || !rc || !a} onClick={send} className="w-full h-11 sm:h-9 bg-stone-800 text-white"><Send className="h-4 w-4 mr-1" />{busy ? "Sending…" : "Send Test SMS"}</Button>
      {!rc && <p className="text-xs text-[#92400E]" data-testid="sms-test-blocked">Choose your designated test phone above first.</p>}
    </div>
  );
}
const STONE = { not_configured: "bg-stone-100 text-stone-700", credentials_configured: "bg-blue-100 text-blue-800", sender_configured: "bg-blue-100 text-blue-800",
  ready_for_test: "bg-[#FEF3C7] text-[#92400E]", active: "bg-emerald-100 text-emerald-800", error: "bg-red-100 text-red-800" };
const Tick = ({ ok }) => (ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> : <XCircle className="h-4 w-4 text-red-600 shrink-0" />);
const FIELDS = [["account_sid", "Account SID", "account_hint"], ["api_key_sid", "API Key SID", "api_key_hint"], ["api_key_secret", "API Key Secret", null],
  ["from_number", "SMS sending number (+1…)", null], ["messaging_service_sid", "or Messaging Service SID", null]];

function Credentials({ st, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const c = st.config;
  const saved = { account_sid: c.present.TWILIO_ACCOUNT_SID, api_key_sid: c.present.TWILIO_API_KEY_SID, api_key_secret: c.present.TWILIO_API_KEY_SECRET,
    from_number: c.present.TWILIO_FROM_NUMBER, messaging_service_sid: c.present.TWILIO_MESSAGING_SERVICE_SID };
  const save = async (clear = false) => {
    setBusy(true);
    try { onSaved(await smsCredentials({ ...f, clear })); setF({}); toast.success(clear ? "Saved credentials cleared" : "Credentials saved securely"); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="sms-credentials" className="space-y-2 text-sm border-t pt-3">
      <p className="font-semibold">Twilio credentials</p>
      <p className="text-xs text-stone-600">Saved values are encrypted on the server and never shown again. Leave a field blank to keep what's saved.</p>
      {FIELDS.map(([k, label, hint]) => (
        <label key={k} className="block text-xs text-stone-600">{label}{saved[k] ? ` · saved${hint && c[hint] ? ` (${c[hint]})` : k === "from_number" && c.sender_hint ? ` (${c.sender_hint})` : ""}` : ""}
          <Input data-testid={`sms-cred-${k}`} type={k === "api_key_secret" ? "password" : "text"} autoComplete="off" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={saved[k] ? "•••••• (saved)" : ""} className="h-11 sm:h-9 mt-1" />
        </label>))}
      <div className="flex gap-2"><Button data-testid="sms-cred-save" disabled={busy || !Object.values(f).some(Boolean)} onClick={() => save(false)} className="h-11 sm:h-9 bg-stone-800 text-white">Save credentials</Button>
        <Button variant="outline" data-testid="sms-cred-clear" disabled={busy} onClick={() => window.confirm("Clear the credentials saved in Bid Management?") && save(true)} className="h-11 sm:h-9">Clear saved</Button></div>
    </div>
  );
}

function Readiness({ st, onVerify, verifying }) {
  const c = st.config;
  const v = st.last_verify;
  return (
    <div data-testid="sms-readiness" className="space-y-1 text-sm">
      <p className="text-xs text-stone-600">Sign-in {c.auth_method === "api_key" ? "API key" : c.auth_method === "auth_token" ? "Auth Token" : "—"} · sender {c.sender === "messaging_service" ? "Messaging Service" : c.sender === "from_number" ? "number" : "—"} {c.sender_hint || ""}</p>
      {c.missing.length > 0 && <p className="text-xs text-stone-600" data-testid="sms-config-missing">Missing: {c.missing.join(", ")}</p>}
      {c.errors.map((e) => <p key={e} className="text-xs text-red-700" data-testid="sms-config-error">{e}</p>)}
      <Button size="sm" variant="outline" data-testid="sms-verify" disabled={verifying || !c.present.TWILIO_ACCOUNT_SID || !c.auth_method} onClick={onVerify} className="h-10 sm:h-8">{verifying ? "Checking…" : "Check connection (no text sent)"}</Button>
      {v && <p data-testid="sms-verify-result" className={`text-xs ${v.ok ? "text-emerald-700" : "text-red-700"}`}>{v.ok ? `Connected · account ${v.account_status} · ${fmtMT(v.checked_at)}` : v.error}</p>}
    </div>
  );
}

function Controls({ st, recipients, onSave }) {
  const [confirm, setConfirm] = useState("");
  const c = st.control;
  const ok = recipients.filter((r) => r.active && r.consent && r.id);
  const save = (patch) => onSave({ ...c, ...patch });
  return (
    <div data-testid="sms-controls" className="space-y-2 text-sm border-t pt-3">
      <label className="flex items-center justify-between gap-2"><span><b>SMS enabled</b> (master switch) — off means nothing is sent</span><Switch data-testid="sms-master" checked={!c.paused} disabled={st.preview_blocked || !st.production_unlocked} onCheckedChange={(v) => save({ paused: !v })} /></label>
      <label className="block text-xs text-stone-600">Designated test phone (consented recipients only)
        <select data-testid="sms-test-recipient" value={c.test_recipient_id || ""} onChange={(e) => save({ test_recipient_id: e.target.value || null })} className="mt-1 h-11 sm:h-9 w-full rounded-md border px-2 bg-white text-sm text-stone-900">
          <option value="">None</option>{ok.map((r) => <option key={r.id} value={r.id}>{r.name} · …{r.phone.slice(-4)}</option>)}
        </select></label>
      <label className="flex items-center gap-2 text-sm"><Checkbox data-testid="sms-approved" checked={c.twilio_approved} onCheckedChange={(v) => save({ twilio_approved: !!v })} />Twilio has approved our business verification{c.twilio_approved_at ? ` (${fmtMT(c.twilio_approved_at)})` : ""}</label>
      <div className="rounded-lg border p-2 space-y-1" data-testid="sms-automation">
        <label className="flex items-center justify-between gap-2"><span><b>Automated auction SMS</b>{c.activated_at ? ` · on since ${fmtMT(c.activated_at)}` : ""}</span>
          <Switch data-testid="sms-auction-toggle" checked={c.auction_alerts} disabled={st.preview_blocked || !st.production_unlocked} onCheckedChange={(v) => save(v ? { auction_alerts: true, confirm } : { auction_alerts: false })} /></label>
        <ul className="text-xs space-y-0.5">{st.automation_requirements.map((x) => <li key={x.id} className="flex gap-1.5 items-start" data-testid={`sms-req-${x.id}`}><Tick ok={x.ok} />{x.label}</li>)}</ul>
        {!c.auction_alerts && !st.preview_blocked && <Input data-testid="sms-enable-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Type ENABLE, then switch on" className="h-10 sm:h-9" />}
        <p className="text-[11px] text-stone-500">Auctions that closed before switching on are never texted. Starts with one recipient only.</p>
      </div>
      {c.saved_in_other_environment && <p className="text-xs text-red-700" data-testid="sms-other-env">Controls were saved in another environment and are ignored here (SMS off).</p>}
    </div>
  );
}

function TestSms({ st, recipients, onSent }) {
  const [busy, setBusy] = useState(false);
  const req = useRef(null);
  const rc = recipients.find((r) => r.id === st.control.test_recipient_id);
  const go = async () => {
    if (!window.confirm(`Send ONE real test text to ${rc.name} (…${rc.phone.slice(-4)}) now?`)) return;
    req.current = req.current || crypto.randomUUID();
    setBusy(true);
    try { const r = await smsTest({ request_id: req.current, confirm: true }); toast.message(TSTATUS[r.status] || r.status); onSent(); }
    catch (e) { toast.error(errText(e)); } finally { req.current = null; setBusy(false); }
  };
  return (
    <div data-testid="sms-test" className="space-y-2 text-sm border-t pt-3">
      <p className="font-semibold">Send Test SMS</p>
      <p className="text-xs text-stone-600">Plain test text to the designated test phone only. It never triggers or affects auction alerts.</p>
      {st.preview_blocked ? <PreviewTest st={st} rc={rc} onSent={onSent} /> : <>
      <Button data-testid="sms-test-send" disabled={busy || !rc || !st.can_send_tests} onClick={go} className="w-full h-11 sm:h-9 bg-stone-800 text-white"><Send className="h-4 w-4 mr-1" />{busy ? "Sending…" : "Send Test SMS"}</Button>
      {!st.production_unlocked ? <p className="text-xs text-[#92400E]" data-testid="sms-test-blocked">SMS production lock is on (SMS_PRODUCTION_ENABLED not set).</p>
        : !st.can_send_tests && <p className="text-xs text-[#92400E]" data-testid="sms-test-blocked">Complete the credentials and switch SMS on first.</p>}</>}
    </div>
  );
}

function TestLog({ rows, onRefresh }) {
  if (!rows.length) return null;
  return (
    <ul data-testid="sms-test-log" className="text-xs divide-y border-t">{rows.map((t) => (
      <li key={t.id} data-testid={`sms-test-row-${t.id}`} className="py-1.5 flex flex-wrap justify-between gap-2">
        <span>{fmtMT(t.created_at)} · {t.event_ref ? `event ${t.event_ref} · ` : ""}{t.recipient_name} {t.to_hint} · <b>{TSTATUS[t.status] || t.status}</b>{t.provider_status ? ` · Twilio: ${DELIVERY[t.provider_status] || t.provider_status}` : ""}{t.provider_id ? ` · ${t.provider_id}` : ""}{t.error ? ` · ${t.error}` : ""}</span>
        {t.provider_id && <Button size="sm" variant="ghost" data-testid={`sms-test-refresh-${t.id}`} onClick={() => onRefresh(t.id)} className="h-8"><RefreshCw className="h-3.5 w-3.5" /></Button>}
      </li>))}</ul>
  );
}

export function SmsConnection({ recipients }) {
  const [st, setSt] = useState(null);
  const [rows, setRows] = useState([]);
  const [verifying, setVerifying] = useState(false);
  const load = useCallback(() => { smsStatus().then(setSt).catch((e) => toast.error(errText(e))); smsTests().then(setRows).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  if (!st) return null;
  const verify = async () => { setVerifying(true); try { await smsVerify(); load(); } catch (e) { toast.error(errText(e)); } finally { setVerifying(false); } };
  const save = async (body) => { try { setSt(await smsControl(body)); toast.success("SMS settings saved"); } catch (e) { toast.error(errText(e)); } };
  const refresh = async (id) => { try { await smsRefresh(id); load(); } catch (e) { toast.error(errText(e)); } };
  return (
    <section data-testid="sms-connection" className="rounded-xl border bg-white p-4 space-y-3">
      <div className="flex flex-wrap justify-between items-center gap-2"><h3 className="font-semibold">Twilio Settings</h3>
        <div className="flex gap-1.5">
          <span data-testid="sms-state" className={`text-[11px] rounded-full px-2 py-0.5 font-semibold ${STONE[st.state]}`}>{st.state_label}</span>
          <span data-testid="sms-env" className={`text-[11px] rounded-full px-2 py-0.5 font-semibold ${st.preview_blocked || !st.production_unlocked ? "bg-[#FEF3C7] text-[#92400E]" : "bg-emerald-100 text-emerald-800"}`}>{st.preview_blocked ? "Preview — sending blocked" : !st.production_unlocked ? "Production — SMS locked" : "Production"}</span>
        </div></div>
      <Readiness st={st} onVerify={verify} verifying={verifying} />
      <Credentials st={st} onSaved={setSt} />
      <Controls st={st} recipients={recipients} onSave={save} />
      <TestSms st={st} recipients={recipients} onSent={load} />
      <SmsTestSession />
      <EventTest st={st} recipients={recipients} onSent={load} />
      <TestLog rows={rows} onRefresh={refresh} />
      <GoLiveChecklist />
    </section>
  );
}
